import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAndroid, FaApple } from 'react-icons/fa';

import { BRAND } from '@/brand/brand';

import Section from '../components/Section';
import { useModalContext } from '../components/ModalProvider';
import { listPushDevices, PushDevice } from '../PushAPI';

/**
 * "Connected devices" in the user profile: the phones where the signed-in user has the app and can
 * receive push, each with the mark of its platform. A device registers itself in the app
 * (Settings → Push); nothing is changed from here.
 */
export default function ProfileDevicesSection() {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const [devices, setDevices] = useState<PushDevice[] | null>(null);

    useEffect(() => {
        let cancelled = false;

        listPushDevices()
            .then((list) => {
                if (!cancelled) setDevices(list);
            })
            .catch((error) => {
                if (!cancelled) {
                    setDevices([]);
                    pushError(error);
                }
            });

        return () => {
            cancelled = true;
        };
        // `pushError` comes from a context value that changes identity on render; the list is loaded once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isIos = (device: PushDevice) => device.platform === 'ios';

    return (
        <Section title={t('settingsPanel.pushPanel.title')}>
            <p className="text-sm text-muted-foreground">{t('settingsPanel.pushPanel.intro', { appName: BRAND.appName })}</p>

            {devices !== null && devices.length === 0 && (
                <p className="mt-4 text-sm">{t('settingsPanel.pushPanel.empty', { appName: BRAND.appName })}</p>
            )}

            {devices !== null && devices.length > 0 && (
                <ul className="mt-4 flex flex-col gap-3" data-testid="profile-devices">
                    {devices.map((device) => (
                        <li key={device.installationId} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span className="text-sm font-semibold">
                                {device.deviceName || t('settingsPanel.pushPanel.unnamedDevice')}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                                {isIos(device) ? (
                                    <FaApple
                                        role="img"
                                        aria-label={t('settingsPanel.pushPanel.platformIos')}
                                        title={t('settingsPanel.pushPanel.platformIos')}
                                        className="h-4 w-4 text-foreground"
                                    />
                                ) : (
                                    <FaAndroid
                                        role="img"
                                        aria-label={t('settingsPanel.pushPanel.platformAndroid')}
                                        title={t('settingsPanel.pushPanel.platformAndroid')}
                                        className="h-4 w-4 text-foreground"
                                    />
                                )}
                                {device.appVersion
                                    ? t('settingsPanel.pushPanel.appVersion', { version: device.appVersion })
                                    : null}
                            </span>
                            <span
                                className={
                                    device.active
                                        ? 'ml-auto text-sm font-medium text-success'
                                        : 'ml-auto text-sm font-medium text-muted-foreground'
                                }
                            >
                                {device.active
                                    ? t('settingsPanel.pushPanel.active')
                                    : t('settingsPanel.pushPanel.inactive')}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </Section>
    );
}
